import { useTranslation } from 'react-i18next'
import { useContext, useEffect, useState, useRef } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Breadcrumbs, Button, LinearProgress, ListItemButton, Typography } from '@mui/material'

import { Pocket } from '../utils/dexie/db'
import { countriesCurrencies } from '../lib'
import { FormError, Payment } from '../types'
import { useSocket } from '../hooks/use-socket'
import Dialog from '../components/common/dialog'
import HelpIcon from '../assets/icons/help-icon.svg'
import NextIcon from '../assets/icons/next-icon.svg'
import Pagination from '../components/common/pagination'
import { MuiOtpInput } from 'mui-one-time-password-input'
import { getPaymentsToReviewData } from '../api/app-data.api'
import { checkTwoFactorAuth, requestAccessToken } from '../api/auth.api'
import { handleSyncDataToLocalDB, handleCheckIfOtpCodeIsValid, upsertManyWithLimitAtStartFast, upsertPaymentWithLimitAtStart } from '../utils'
import { FiltersOptionsModal } from '../components/common/filters-options-modal'
import { discardReviewedPayment, sendReviewedPayment } from '../api/payments.api'
import { ActionsBar } from '../components/payments-to-review-management/actions-bar'
import { PaymentToReviewListItem } from '../components/payments-to-review-management'
import { AppStateContext, AuthContext, NotificationsContext, UserContext } from '../contexts'
import CustomerDetailsPaymentDetailsDataTable from '../components/customer-details-management/customer-details-payment-details-data-table'
import { baseButtonBlueBg } from '../theme/custom-styles'
import { searchPaymentsByRegex } from '../api/app-queries.api'

const PaymentsToReviewManagement = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

  const navigate = useNavigate()
  const location = useLocation()
  const { documentId } = useParams()
  const { userSession } = useContext(UserContext)
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { updateNewNotificationStatus } = useContext(NotificationsContext)
  const { t } = useTranslation(['payments-to-review-management', 'common'])
  const { setScrollToTop, customersBriefData, currentPocketsData } = useContext(AppStateContext)

  const currentListPageRef = useRef<number>(1)
  const paymentToSearchRef = useRef<string>('')
  const dataFullyLoadedRef = useRef<boolean>(false)
  const currentLoadingDataRef = useRef<boolean>(true)
  const paymentsPocketDataRef = useRef<Array<any>>([])
  const showPocketsPaymentsRef = useRef<boolean>(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const paymentStatusAllowedRef = useRef<Array<string>>([])
  const currentPaymentsDataRef = useRef<Array<Payment>>([])
  const noRegistriesFoundAtDatabaseRef = useRef<boolean>(false)

  const [otpCode, setOtpCode] = useState('')
  const selectedPocketIdRef = useRef<string>('')
  const [errors, setErrors] = useState<FormError>({})
  const [pageSize, setPageSize] = useState<number>(50)
  const [hasMultipleRows, setHasMultipleRows] = useState(false)
  const [currentListPage, setCurrentListPage] = useState<number>(1)
  const [paymentToSearch, setPaymentToSearch] = useState<string>('')
  const [selectedPocketId, setSelectedPocketId] = useState<string>('')
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  const [paymentsList, setPaymentsList] = useState<Array<Partial<Payment>>>([])
  const [paymentsData, setPaymentsData] = useState<Array<Partial<Payment>>>([])
  const [searchingInDatabase, setSearchingInDatabase] = useState<boolean>(false)
  const [wantsToDiscardPayment, setWantsToDiscardPayment] = useState<boolean>(false)
  const [searchedPayments, setSearchedPayments] = useState<Array<Partial<Payment>>>([])
  const [openPaymentDetailsModal, setOpenPaymentDetailsModal] = useState<boolean>(false)
  const [openFiltersOptionsModal, setOpenFiltersOptionsModal] = useState<boolean>(false)
  const [noRegistriesFoundAtLocal, setNoRegistriesFoundAtLocal] = useState<boolean>(false)
  const [paymentsPocketData, setPaymentsPocketData] = useState<Array<Partial<Payment>>>([])
  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState<Partial<Payment>>({})
  const [noRegistriesFoundAtDatabase, setNoRegistriesFoundAtDatabase] = useState<boolean>(false)
  const [openSendPaymentConfimationModal, setOpenSendPaymentConfimationModal] = useState<boolean>(false)
  const [dialogSendPaymentConfimationIsLoading, setDialogSendPaymentConfimationIsLoading] = useState<boolean>(false)

  useEffect(() => {
    if (!appDataSocket) return

    if (!userSession) return

    paymentStatusAllowedRef.current = userSession.subRole === 'compliance-specialist' ? ['for-review', 'waiting-for-resolution'] : ['reviewed', 'for-review', 'waiting-for-resolution']

    if (documentId) {
      appDataSocket.on(`updatePaymentsToReview:${documentId}`, async (dataFromSocket) => {
        if (!paymentStatusAllowedRef.current.includes(dataFromSocket.status) || (paymentStatusAllowedRef.current.includes(dataFromSocket.status) && dataFromSocket.deleted === true)) {
          handleDeleteAndSetPayments(String(dataFromSocket._id))
          return
        }

        handleUpdateAndSetPayments(dataFromSocket)
      })

      appDataSocket.on(`deletePaymentsToReview:${documentId}`, async (dataFromSocket) => {
        if (paymentStatusAllowedRef.current.includes(dataFromSocket.status)) {
          handleDeleteAndSetPayments(String(dataFromSocket.documentKey))
        }
      })
    }

    if (!documentId) {
      appDataSocket.on(`newPaymentAdded:${selectedCurrency}`, async (dataFromSocket: any) => {
        if (!paymentStatusAllowedRef.current.includes(dataFromSocket.status)) {
          handleDeleteAndSetPayments(String(dataFromSocket._id))
          return
        }

        handleUpdateAndSetPayments(dataFromSocket)
      })

      appDataSocket.on(`paymentUpdated:${selectedCurrency}`, async (dataFromSocket: any) => {
        if (!paymentStatusAllowedRef.current.includes(dataFromSocket.status) || (paymentStatusAllowedRef.current.includes(dataFromSocket.status) && dataFromSocket.deleted === true)) {
          handleDeleteAndSetPayments(String(dataFromSocket._id))
          return
        }

        handleUpdateAndSetPayments(dataFromSocket)
      })

      appDataSocket.on(`paymentDeleted:${selectedCurrency}`, async (dataFromSocket: any) => {
        if (paymentStatusAllowedRef.current.includes(dataFromSocket.status)) {
          handleDeleteAndSetPayments(String(dataFromSocket.paymentId))
        }
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, appDataSocket, isAppDataSocketInitialized])

  useEffect(() => {
    handleGetPaymentsToReview()

    const checkRows = () => {
      if (!containerRef.current) return

      const container = containerRef.current
      const firstItem = container.firstElementChild
      if (!firstItem) return

      const firstItemTop = firstItem.getBoundingClientRect().top
      const multipleRows = Array.from(container.children).some((item) => item.getBoundingClientRect().top > firstItemTop)

      setHasMultipleRows(multipleRows)
    }

    const resizeObserver = new ResizeObserver(checkRows)

    if (userSession && userSession.belongsTo !== 'holdings' && userSession.subRole && userSession.subRole === 'compliance-specialist') {
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current)
      }
    }

    return () => resizeObserver.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession])

  useEffect(() => {
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const paymentsPerPage =
      paymentToSearch === '' && searchedPayments.length === 0 && !showPocketsPaymentsRef.current
        ? paymentsData.slice(firstPageIndex, lastPageIndex)
        : paymentToSearch === '' && showPocketsPaymentsRef.current
          ? paymentsPocketData.slice(firstPageIndex, lastPageIndex)
          : searchedPayments.slice(firstPageIndex, lastPageIndex)

    setPaymentsList(paymentsPerPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentListPage, paymentsData, searchedPayments, paymentsPocketData])

  useEffect(() => {
    setCurrentListPage(1)
    currentListPageRef.current = 1

    if (selectedPocketId) {
      selectedPocketIdRef.current = selectedPocketId
      handleFilterPaymentsByPocket()
      showPocketsPaymentsRef.current = true
      return
    }

    selectedPocketIdRef.current = ''
    showPocketsPaymentsRef.current = false
    paymentsPocketDataRef.current = []
    setPaymentsPocketData([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPocketId])

  const handleDeleteAndSetPayments = (paymentId: string) => {
    const paymentsDataUpdated = currentPaymentsDataRef.current.filter((paymentData: Payment) => String(paymentData._id) !== String(paymentId))

    if (documentId) {
      globalThis.sessionStorage.setItem(`paymentsToReview:${documentId}`, JSON.stringify({ paymentsData: paymentsDataUpdated }))
    } else {
      globalThis.sessionStorage.setItem(`paymentsToReview:${selectedCountry}`, JSON.stringify({ paymentsData: paymentsDataUpdated }))
    }

    handleSetInitialData({ paymentsData: paymentsDataUpdated })
  }

  const handleUpdateAndSetPayments = (paymentData: Payment) => {
    let localPaymentsToReviewData = null

    if (documentId) {
      localPaymentsToReviewData = globalThis.sessionStorage.getItem(`paymentsToReview:${documentId}`) || JSON.stringify({ paymentsData: [] })
    } else {
      localPaymentsToReviewData = globalThis.sessionStorage.getItem(`paymentsToReview:${selectedCountry}`) || JSON.stringify({ paymentsData: [] })
    }

    const paymentsDataParsed = JSON.parse(localPaymentsToReviewData)
    const paymentsDataUpdated = upsertPaymentWithLimitAtStart(paymentsDataParsed.paymentsData, paymentData, 500)

    handleSetInitialData({ paymentsData: paymentsDataUpdated })
  }

  const handleGetPaymentsToReview = async () => {
    let paymentsToReviewDataToSet: any = {}
    const localPaymentsToReviewData = documentId ? globalThis.sessionStorage.getItem(`paymentsToReview:${documentId}`) : globalThis.sessionStorage.getItem(`paymentsToReview:${selectedCountry}`)

    if (localPaymentsToReviewData) {
      paymentsToReviewDataToSet = JSON.parse(localPaymentsToReviewData)
      handleSetInitialData(paymentsToReviewDataToSet)
    }

    handleGetPaymentsToReviewFromRemote()
  }

  const handleGetPaymentsToReviewFromRemote = async () => {
    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getPaymentsToReviewData({ documentId, selectedCountry, selectedCurrency }, accessToken)

      handleSetInitialData(data)

      if (documentId) {
        globalThis.sessionStorage.setItem(`paymentsToReview:${documentId}`, JSON.stringify(data))
      } else {
        globalThis.sessionStorage.setItem(`paymentsToReview:${selectedCountry}`, JSON.stringify(data))
      }
    } catch (error) {
      console.log('Error handleGetPaymentsToReview > ', error)
    }
  }

  const handleSetInitialData = async (dataToSet: any) => {
    if (dataToSet.paymentsData.length === 0 && documentId) {
      navigate(`/document-details/${documentId}`, { replace: true })
      return
    }

    if (documentId) {
      const paymentsToReviewCount = dataToSet.paymentsData.filter((payment: Payment) => payment.status && payment.status !== 'reviewed').length

      if (paymentsToReviewCount === 0) {
        navigate(`/document-details/${documentId}`, { replace: true })
        return
      }
    }

    const currentListPageDataState = currentListPageRef.current
    const currentPaymentToSearchState = paymentToSearchRef.current

    if (currentPaymentToSearchState === '' || currentPaymentToSearchState === null) {
      const firstPageIndex = currentListPageDataState === null ? 0 : (currentListPageDataState - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const paymentsPerPage = dataToSet.paymentsData.slice(firstPageIndex, lastPageIndex)

      setPaymentsList(paymentsPerPage as Array<Payment>)
      for (const element of paymentsPerPage) {
        handleSyncDataToLocalDB('payments', element)
      }
    }

    currentPaymentsDataRef.current = dataToSet.paymentsData
    setPaymentsData(dataToSet.paymentsData as Array<Payment>)

    if (currentLoadingDataRef.current === true) {
      currentLoadingDataRef.current = false
      setLoadingLocalData(false)
    }

    dataFullyLoadedRef.current = true
  }

  const handleFilterPaymentsByPocket = () => {
    const paymentsDataByPocket = [...(currentPaymentsDataRef.current as Array<any>)].filter((paymentData) => {
      if (paymentData.fundsFromPocket && String(paymentData.fundsFromPocket) === String(selectedPocketIdRef.current)) {
        return paymentData
      }

      return false
    })

    paymentsPocketDataRef.current = paymentsDataByPocket
    setPaymentsPocketData(paymentsDataByPocket)
  }

  const shouldTrigger = (input: string): boolean => {
    const t = input.toLowerCase()

    if (!t.includes('ale')) return false // fast pre-check

    return /\bale(rta?|rt?)?\b/.test(t)
  }

  const handleSetPaymentToSearch = (paymentToSearchData: string, showError: boolean) => {
    currentListPageRef.current = 1
    paymentToSearchRef.current = paymentToSearchData

    if (noRegistriesFoundAtDatabaseRef.current === true) {
      setNoRegistriesFoundAtDatabase(false)
    }

    setErrors({})
    setCurrentListPage(1)
    setNoRegistriesFoundAtLocal(false)
    setPaymentToSearch(paymentToSearchData)

    if (paymentToSearchData === '') {
      const firstPageIndex = (currentListPage - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const currentPaymentsDataState = currentPaymentsDataRef.current ?? []
      const paymentsPerPage = currentPaymentsDataState.slice(firstPageIndex, lastPageIndex)

      setPaymentsList(paymentsPerPage)
      setSearchedPayments([])

      if (showError) setErrors({ paymentToSearch: t('searchField.errors.emptyField') })
      return
    }

    const currentFullPaymentsData = selectedPocketIdRef.current === '' ? [...currentPaymentsDataRef.current] : [...(paymentsPocketDataRef.current as Array<Payment>)]
    const filteredPayments = currentFullPaymentsData.filter((paymentData) => {
      const customerRegisteredFullName = paymentData.customerData?.registeredFullName ? paymentData.customerData.registeredFullName : ''
      const customerDocumentNumber = paymentData.customerData?.documentNumber ? paymentData.customerData.documentNumber : ''
      const paymentReferenceNumber = paymentData.referenceNumber

      const customerBanksAccountsData = Array.isArray(paymentData.customerData?.bankAccountsData)
        ? paymentData.customerData?.bankAccountsData.filter((bankAccountData) => {
            return bankAccountData.bankNumber.toLowerCase().includes(paymentToSearchData.toLowerCase())
          })
        : []

      return (
        customerRegisteredFullName.toLowerCase().includes(paymentToSearchData.toLowerCase()) ||
        customerDocumentNumber.toLowerCase().includes(paymentToSearchData.toLowerCase()) ||
        paymentReferenceNumber.toLowerCase().includes(paymentToSearchData.toLowerCase()) ||
        customerBanksAccountsData.length > 0 ||
        (shouldTrigger(paymentToSearchData) && Number(paymentData.novelties?.length) > 0 && (paymentData.status === 'for-review' || paymentData.status === 'waiting-for-resolution'))
      )
    })

    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const paymentsPerPage = filteredPayments.slice(firstPageIndex, lastPageIndex)

    setPaymentsList(paymentsPerPage)
    setSearchedPayments(filteredPayments)

    if (filteredPayments.length === 0) {
      setNoRegistriesFoundAtLocal(true)
    }
  }

  const handleDiscardPayment = async () => {
    const { otpCodeIsValid, formErrors } = handleCheckIfOtpCodeIsValid(otpCode, t)

    if (!otpCodeIsValid) {
      setErrors({ ...formErrors })
      return
    }

    try {
      setErrors({})
      setDialogSendPaymentConfimationIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await checkTwoFactorAuth(otpCode, accessToken)
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogSendPaymentConfimationIsLoading(false)
      return
    }

    try {
      setOtpCode('')
      setErrors({})

      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await discardReviewedPayment(String(selectedPaymentDetails._id), accessToken)

      setOpenPaymentDetailsModal(false)
      setOpenSendPaymentConfimationModal(false)
      setDialogSendPaymentConfimationIsLoading(false)
      updateNewNotificationStatus?.(true, 'payment-deleted-success')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message, Object.keys(error))
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else if (error?.response?.status === 406) {
        setErrors({ generalError: t('formGeneralErrors.insufficientAdamoBalance', { ns: 'common' }) })
      } else if (error?.response?.status === 400) {
        setErrors({ generalError: t('formGeneralErrors.insufficientBalance', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogSendPaymentConfimationIsLoading(false)
    }
  }

  const handleSendPayment = async () => {
    const { otpCodeIsValid, formErrors } = handleCheckIfOtpCodeIsValid(otpCode, t)

    if (!otpCodeIsValid) {
      setErrors({ ...formErrors })
      return
    }

    try {
      setErrors({})
      setDialogSendPaymentConfimationIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await checkTwoFactorAuth(otpCode, accessToken)
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      return setDialogSendPaymentConfimationIsLoading(false)
    }

    try {
      setErrors({})
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await sendReviewedPayment(String(selectedPaymentDetails._id), accessToken)

      setOpenPaymentDetailsModal(false)
      setOpenSendPaymentConfimationModal(false)
      setDialogSendPaymentConfimationIsLoading(false)
      navigate(`/customer-company-payments/${String(userSession?.belongsTo)}`)
      updateNewNotificationStatus?.(true, 'payment-sent-successfully')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message, Object.keys(error))
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else if (error?.response?.status === 406) {
        setErrors({ generalError: t('formGeneralErrors.insufficientAdamoBalance', { ns: 'common' }) })
      } else if (error?.response?.status === 400) {
        setErrors({ generalError: t('formGeneralErrors.insufficientBalance', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogSendPaymentConfimationIsLoading(false)
    }
  }

  const handleSearchPaymentsToReviewDataByRegex = async () => {
    try {
      setErrors({})
      setSearchingInDatabase(true)

      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))

      const { data } = await searchPaymentsByRegex({ documentId: String(documentId), searchText: paymentToSearch, status: 'to-review', belongsTo: userSession?.belongsTo, selectedCountry, selectedCurrency }, accessToken)

      if (data.paymentsData.length === 0) {
        setSearchingInDatabase(false)
        setNoRegistriesFoundAtDatabase(true)
        noRegistriesFoundAtDatabaseRef.current = true
        return
      }

      /* ESTO ES PARA ACTUALIZAR EL LISTADO SOLO CON LOS BUSCADOS */
      data.paymentsData.sort((thisDocument: Partial<Payment>, nextDocument: Partial<Payment>) => {
        return Date.parse(String(nextDocument.createdAt)) - Date.parse(String(thisDocument.createdAt))
      })
      setPaymentsList(data.paymentsData)
      setSearchedPayments(data.paymentsData)
      /* ESTO ES PARA ACTUALIZAR EL LISTADO SOLO CON LOS BUSCADOS */

      const currentPaymentsData = [...currentPaymentsDataRef.current]
      const paymentsDataUpdated = upsertManyWithLimitAtStartFast(currentPaymentsData, data.paymentsData, 1000)
      paymentsDataUpdated.sort((thisDocument: Partial<Payment>, nextDocument: Partial<Payment>) => {
        return Date.parse(String(nextDocument.createdAt)) - Date.parse(String(thisDocument.createdAt))
      })

      currentPaymentsDataRef.current = paymentsDataUpdated
      setPaymentsData(paymentsDataUpdated)

      setSearchingInDatabase(false)
      setNoRegistriesFoundAtLocal(false)

      if (documentId) {
        globalThis.sessionStorage.setItem(`paymentsToReview:${documentId}`, JSON.stringify({ paymentsData: paymentsDataUpdated }))
      } else {
        globalThis.sessionStorage.setItem(`paymentsToReview:${selectedCountry}`, JSON.stringify({ paymentsData: paymentsDataUpdated }))
      }
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setSearchingInDatabase(false)
      return
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <div className="section-container">
      <Breadcrumbs
        separator={
          <img
            src={NextIcon}
            alt="next-icon"
          />
        }
        aria-label="breadcrumb"
      >
        {userSession &&
        (userSession.role === 'super-admin-client' || userSession.role === 'admin-client') &&
        location.state?.previousPath &&
        location.state?.previousPath === `customer-company-payments/${userSession.belongsTo}` ? (
          <ListItemButton
            sx={{
              '&.MuiButtonBase-root': {
                padding: '0px'
              },
              '&.MuiButtonBase-root:hover': {
                backgroundColor: 'rgba(0,0,0,0)'
              }
            }}
            onClick={() => navigate('/')}
          >
            <Typography
              key="3"
              className="breadcrumb-inactive"
            >
              {t('breadcrumbs.home')}
            </Typography>
          </ListItemButton>
        ) : userSession && (userSession.role === 'super-admin-client' || userSession.role === 'admin-client') && location.state?.previousPath && location.state?.previousPath === 'insights' ? (
          <ListItemButton
            sx={{
              '&.MuiButtonBase-root': {
                padding: '0px'
              },
              '&.MuiButtonBase-root:hover': {
                backgroundColor: 'rgba(0,0,0,0)'
              }
            }}
            onClick={() => navigate('/insights')}
          >
            <Typography
              key="3"
              className="breadcrumb-inactive"
            >
              {t('breadcrumbs.insights')}
            </Typography>
          </ListItemButton>
        ) : (
          <Typography
            key="3"
            className="breadcrumb-inactive"
          >
            {t('breadcrumbs.firstLevel')}
          </Typography>
        )}
        {userSession &&
          (userSession.role === 'super-admin-client' || userSession.role === 'admin-client') &&
          location.state?.previousPath &&
          location.state?.previousPath === `customer-company-payments/${userSession.belongsTo}` && (
            <ListItemButton
              sx={{
                '&.MuiButtonBase-root': {
                  padding: '0px'
                },
                '&.MuiButtonBase-root:hover': {
                  backgroundColor: 'rgba(0,0,0,0)'
                }
              }}
              onClick={() => navigate(`/customer-company-payments/${userSession.belongsTo}`)}
            >
              <Typography
                key="3"
                className="breadcrumb-inactive"
              >
                {t('breadcrumbs.individualPayments')}
              </Typography>
            </ListItemButton>
          )}
        <Typography
          key="3"
          className="breadcrumb-active"
        >
          {userSession?.role === 'super-admin-client' && location.state?.previousPath && location.state?.previousPath === `customer-company-payments/${userSession.belongsTo}`
            ? t('breadcrumbs.pending')
            : t('breadcrumbs.secondLevel')}
        </Typography>
      </Breadcrumbs>
      {userSession && userSession.belongsTo !== 'holdings' && userSession.subRole && userSession.subRole === 'compliance-specialist' && (
        <div
          ref={containerRef}
          className={`pockets-submenu-container ${hasMultipleRows ? 'has-multiple-rows' : ''}`}
        >
          <div
            onClick={() => setSelectedPocketId('')}
            className={`button-item ${String(selectedPocketId) === '' ? 'button-item-selected' : ''}`}
          >
            {t('filters.all', { ns: 'system-users-management' })?.toUpperCase()}
          </div>
          {currentPocketsData?.map((pocketData: Pocket, index: number) => {
            return (
              <div
                key={index}
                onClick={() => setSelectedPocketId(String(pocketData._id))}
                className={`button-item ${String(pocketData._id) === String(selectedPocketId) ? 'button-item-selected' : ''}`}
              >
                {pocketData.name?.toUpperCase()}
              </div>
            )
          })}
        </div>
      )}
      <ActionsBar
        errors={errors}
        processingDocument={true} // --> Este es para ocultar siempre el botón de borrar desde la parte superior
        paymentsData={paymentsData}
        paymentToSearch={paymentToSearch}
        className="payments-to-review-app-bar"
        paymentsPocketData={paymentsPocketData}
        searchingInDatabase={searchingInDatabase}
        handleClearSearchInput={() => setErrors({})}
        showPocketsPayments={showPocketsPaymentsRef.current}
        handleSetPaymentToSearch={handleSetPaymentToSearch}
        handleOpenFiltersOptionsModal={() => setOpenFiltersOptionsModal(true)}
      />
      <div className="table-header-container">
        <div className="customer-company-table-column customer-company-table-width-10 display-none-xlg table-header-text">{t('tableHeaders.date')}</div>
        <div className="customer-company-table-column customer-company-table-width-45 table-header-text customer-company-table-width-70-md-down">{t('tableHeaders.beneficiary')}</div>
        <div className="customer-company-table-column customer-company-table-width-45 table-header-text customer-company-table-width-30-md-down">{t('tableHeaders.novelties')}</div>
      </div>
      {loadingLocalData ? (
        <div className="documents-list-container">
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
        </div>
      ) : (
        <>
          <div className="documents-list-container">
            {paymentsList.map((paymentData: Partial<Payment>) => {
              return (
                <PaymentToReviewListItem
                  key={paymentData._id}
                  paymentData={paymentData}
                  handleSetSelectedPayment={(paymentData: Partial<Payment>) => {
                    setOpenPaymentDetailsModal(true)
                    setSelectedPaymentDetails(paymentData)
                  }}
                  handleGoToPaymentDetails={(paymentId: string) => navigate(`/payment-to-review-details/${paymentId}`)}
                />
              )
            })}
          </div>
          <Pagination
            pageSize={pageSize}
            currentPage={currentListPage}
            handlePaginationPageChange={(page: number) => {
              setTimeout(() => {
                setScrollToTop(true)
                setCurrentListPage(page)
                currentListPageRef.current = page
              }, 100)
            }}
            handleChangePageSize={(pageSizeToSet: string) => {
              setScrollToTop(true)
              setPageSize(Number(pageSizeToSet))
            }}
            totalCount={
              paymentToSearch === '' && searchedPayments.length === 0 && !showPocketsPaymentsRef.current
                ? paymentsData.length
                : paymentToSearch === '' && showPocketsPaymentsRef.current
                  ? paymentsPocketData.length
                  : searchedPayments.length
            }
          />
          {noRegistriesFoundAtLocal && (
            <div className="customer-no-data-found-container">
              {searchingInDatabase ? (
                <>
                  <div className="customer-no-data-found-searching-progress-bar">
                    <LinearProgress />
                  </div>
                  <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case3', { ns: 'customer-company-details-management' })}</div>
                </>
              ) : (
                <>
                  {noRegistriesFoundAtDatabase === false ? (
                    <>
                      <div className="customer-no-data-found-no-registries-text">
                        {t('searchingResultTexts.case1.firstPart', { ns: 'customer-company-details-management' })}{' '}
                        <strong
                          className="customer-no-data-found-no-registries-text-click"
                          onClick={() => handleSearchPaymentsToReviewDataByRegex()}
                        >
                          {t('searchingResultTexts.case1.thirdPart', { ns: 'customer-company-details-management' })}
                        </strong>{' '}
                        {t('searchingResultTexts.case1.fourthPart', { ns: 'customer-company-details-management' })}
                      </div>
                      <Button
                        sx={baseButtonBlueBg}
                        onClick={handleSearchPaymentsToReviewDataByRegex}
                      >
                        {t('searchingResultTexts.expandSearch', { ns: 'customer-company-details-management' })}
                      </Button>
                      {errors.generalError && (
                        <div
                          style={{ marginTop: '0px' }}
                          className="error-message"
                        >
                          {errors.generalError}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="customer-no-data-found-no-registries-text">
                        <img
                          src={HelpIcon}
                          alt="help-icon"
                        />
                      </div>
                      <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case2', { ns: 'customer-company-details-management' })}</div>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
      {openFiltersOptionsModal && (
        <FiltersOptionsModal
          showNoveltyTypeFilter
          filtersToApply="payments"
          customersData={(customersBriefData as any)?.customersData}
          handleDialogOpenState={(openState: boolean) => {
            setOpenFiltersOptionsModal(openState)
          }}
          title={t('filtersOptionsModal.payments.title', { ns: 'common' })}
          actionButtonText={t('filtersOptionsModal.payments.actionButtonText', { ns: 'common' })}
        />
      )}
      {openPaymentDetailsModal && (
        <Dialog
          errors={errors}
          closeOptionWarning
          closeButtonText={t('paymentDetailsModal.discardPayment', { ns: 'document-payments-details-management' })}
          hideActionsButtons={
            userSession?.role.includes('-builder') ||
            (selectedPaymentDetails.status !== 'paid' && selectedPaymentDetails.status !== 'reviewed') ||
            (selectedPaymentDetails.status === 'reviewed' && selectedPaymentDetails.documentId !== '')
          }
          dialogTitle={t('paymentDetailsModal.title', { ns: 'document-payments-details-management' })}
          handleDialogAction={() => {
            if (selectedPaymentDetails.status === 'reviewed') {
              setOpenSendPaymentConfimationModal(true)
            }
          }}
          handleCloseOptionAction={() => {
            setErrors({})

            if (selectedPaymentDetails.status === 'reviewed') {
              setWantsToDiscardPayment(true)
              setOpenSendPaymentConfimationModal(true)
              return
            }

            setOpenPaymentDetailsModal(false)
            setSelectedPaymentDetails({})
          }}
          handleDialogOpenState={(openState: boolean) => {
            if (openState === false) {
              setWantsToDiscardPayment(false)
            }

            setErrors({})
            setOpenPaymentDetailsModal(openState)
            setSelectedPaymentDetails({})
          }}
          actionButtonText={
            selectedPaymentDetails.status === 'paid'
              ? t('paymentDetailsModal.actionButtonText', { ns: 'document-payments-details-management' })
              : t('paymentDetailsModal.sendActionButtonText', { ns: 'document-payments-details-management' })
          }
        >
          <CustomerDetailsPaymentDetailsDataTable paymentDetailsData={selectedPaymentDetails} />
        </Dialog>
      )}
      {openSendPaymentConfimationModal && (
        <Dialog
          errors={errors}
          handleDialogAction={() => {
            if (wantsToDiscardPayment) {
              handleDiscardPayment()
              return
            }

            handleSendPayment()
          }}
          actionButtonWarningStyle={wantsToDiscardPayment}
          actionButtonSuccessStyle={!wantsToDiscardPayment}
          dialogTitle={t('confirmSendPaymentModal.title')}
          dialogIsLoading={dialogSendPaymentConfimationIsLoading}
          actionButtonText={
            wantsToDiscardPayment ? t('confirmSendPaymentModal.rejectActionButtonText', { ns: 'payments-to-review-management' }) : t('confirmSendPaymentModal.actionButtonText', { ns: 'payments-to-review-management' })
          }
          handleDialogOpenState={(openState: boolean) => {
            if (openState === false) {
              setErrors({})
              setOtpCode('')
              setWantsToDiscardPayment(false)
            }

            setOpenSendPaymentConfimationModal(openState)
          }}
        >
          <div
            className="step"
            style={{ marginTop: '0px', gap: '16px' }}
          >
            <div
              className="step-instruction-container"
              style={{ fontSize: '14px', color: '#F59E0B' }}
            >
              {t('checkCodeInstruction', { ns: 'two-factor-auth' })}
            </div>
            <MuiOtpInput
              length={6}
              value={otpCode}
              onChange={(newValue: string) => setOtpCode(newValue)}
              className={errors?.otpCodeError ? 'MuiOtpInput-Box-inner MuiOtpInput-Box-error' : 'MuiOtpInput-Box-inner'}
            />
          </div>
        </Dialog>
      )}
    </div>
  )
}

export default PaymentsToReviewManagement
